import { getToken, getCompany } from "../../../../shared/data/storage/authStorage";
import {
  CreditNoteDataEntity,
  CreditNoteDocumentType,
  CreditNoteListStatus,
} from "../../domain/entities/CreditNoteEntity";
import { API_BASE_URL } from "../../../../config/api";

// HTTP Status Codes
const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

// API Error Class
class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

export interface CreditNotesQueryParams {
  limit?: number;
  offset?: number;
  status?: CreditNoteListStatus;
  search?: string;
  date_from?: string;
  date_to?: string;
  sort_by?: "created_at" | "updated_at" | "document_number" | "status";
  sort_order?: "asc" | "desc";
  document_type?: CreditNoteDocumentType;
}

/**
 * Fetch credit notes through the unified list endpoint.
 */
export const fetchCreditNotes = async (
  params: CreditNotesQueryParams = {}
): Promise<any> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  const queryParams = new URLSearchParams();
  if (params.limit !== undefined) {
    queryParams.append("limit", Math.min(params.limit, 100).toString());
  }
  if (params.offset !== undefined) {
    queryParams.append("offset", params.offset.toString());
  }
  if (params.status) {
    queryParams.append("status", params.status);
  }
  if (params.search) {
    queryParams.append("search", params.search);
  }
  if (params.date_from) {
    queryParams.append("date_from", params.date_from);
  }
  if (params.date_to) {
    queryParams.append("date_to", params.date_to);
  }
  if (params.sort_by) {
    queryParams.append("sort_by", params.sort_by);
  }
  if (params.sort_order) {
    queryParams.append("sort_order", params.sort_order);
  }
  if (params.document_type && params.document_type !== "all") {
    queryParams.append("document_type", params.document_type);
  }

  const url = `${API_BASE_URL}/api/credit-notes${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `HTTP error! status: ${response.status}`,
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Update Credit Note JSON
 * Updates the JSON field of a credit note (for auto-save)
 */
export const updateCreditNoteJson = async (
  creditNoteId: number,
  creditNoteJson: CreditNoteDataEntity
): Promise<any> => {
  const token = getToken();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/credit-notes/${creditNoteId}/update-json`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        credit_note_id: creditNoteId,
        credit_note_json: creditNoteJson,
      }),
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to update credit note JSON",
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Create Credit Note in Zoho Books
 * Creates a new credit note in the database and pushes it to Zoho Books
 * If creditNoteId is provided, updates the existing credit note instead of creating a new one
 * Note: For updating existing credit notes, use updateZohoCreditNote()
 * @param creditNoteJson - Credit note data
 * @param pdfUrl - Optional PDF URL
 * @param creditNoteId - Optional existing credit note ID (for updating drafts)
 */
export const createZohoCreditNote = async (
  creditNoteJson: CreditNoteDataEntity,
  pdfUrl?: string,
  creditNoteId?: number
): Promise<any> => {
  const token = getToken();
  const companyData = getCompany();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const requestBody: any = {
    credit_note_json: creditNoteJson,
    pdf_url: pdfUrl,
    company_gst_enabled: companyData?.companyGstEnabled,
  };

  // Include credit_note_id if provided (for updating existing drafts)
  if (creditNoteId) {
    requestBody.credit_note_id = creditNoteId;
  }

  const response = await fetch(
    `${API_BASE_URL}/api/credit-notes/push-to-zoho`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to create credit note in Zoho",
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Update Credit Note in Zoho Books
 */
export const updateZohoCreditNote = async (
  creditNoteId: number | string,
  creditNoteJson: CreditNoteDataEntity
): Promise<any> => {
  const token = getToken();
  const companyData = getCompany();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/credit-notes/${creditNoteId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        credit_note_id: creditNoteId,
        credit_note_json: creditNoteJson,
        company_gst_enabled: companyData?.companyGstEnabled,
      }),
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to update credit note in Zoho",
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Reject Credit Note
 */
export const rejectCreditNote = async (creditNoteId: number): Promise<any> => {
  const token = getToken();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/credit-notes/${creditNoteId}/reject`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to reject credit note",
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Approve Credit Note
 */
export const approveCreditNote = async (creditNoteId: number): Promise<any> => {
  const token = getToken();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/credit-notes/${creditNoteId}/approve`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to approve credit note",
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Get Credit Note by ID
 * Fetches a single credit note with all its data including status and zoho_id
 */
export const getCreditNoteById = async (
  creditNoteId: number | string
): Promise<any> => {
  const token = getToken();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/credit-notes/${creditNoteId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to fetch credit note",
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Save Credit Note As Draft
 * Creates a new credit note with draft status (0)
 */
export const saveCreditNoteAsDraft = async (
  creditNoteJson: CreditNoteDataEntity,
  options?: {
    jobid?: string | null;
    folder_id?: string | null;
    company_id?: number | null;
    gst_rates?: any;
    gst_types?: any;
    is_tds_applied?: number;
    tds_tax_id?: string | null;
    tds_tax_amount?: number | null;
    tds_tax_percentage?: number | null;
    tds_tax_name?: string | null;
    tds_section?: string | null;
  }
): Promise<any> => {
  const token = getToken();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const response = await fetch(`${API_BASE_URL}/api/credit-notes/save-draft`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      json: creditNoteJson,
      jobid: options?.jobid || null,
      folder_id: options?.folder_id || null,
      company_id: options?.company_id || null,
      gst_rates: options?.gst_rates || null,
      gst_types: options?.gst_types || null,
      is_tds_applied: options?.is_tds_applied || 0,
      tds_tax_id: options?.tds_tax_id || null,
      tds_tax_amount: options?.tds_tax_amount || null,
      tds_tax_percentage: options?.tds_tax_percentage || null,
      tds_tax_name: options?.tds_tax_name || null,
      tds_section: options?.tds_section || null,
    }),
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to save credit note as draft",
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Fetch credit note counts for dashboard cards.
 */
export const fetchCreditNoteCounts = async (params?: {
  date_from?: string;
  date_to?: string;
  document_type?: CreditNoteDocumentType;
}): Promise<{
  pending_count: number;
  posted_count: number;
  rejected_count: number;
  under_review_count: number;
}> => {
  try {
    const token = getToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED
      );
    }

    const queryParams = new URLSearchParams();
    if (params?.date_from) {
      queryParams.append("date_from", params.date_from);
    }
    if (params?.date_to) {
      queryParams.append("date_to", params.date_to);
    }
    if (params?.document_type && params.document_type !== "all") {
      queryParams.append("document_type", params.document_type);
    }

    const response = await fetch(
      `${API_BASE_URL}/api/credit-notes/counts${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData
      );
    }

    const data = await response.json();
    return {
      pending_count: data.data?.pending_count || 0,
      posted_count: data.data?.posted_count || 0,
      rejected_count: data.data?.rejected_count || 0,
      under_review_count: data.data?.under_review_count || 0,
    };
  } catch (error) {
    console.error("Error fetching credit note counts:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

/**
 * Delete credit note (unified endpoint)
 * POST /api/credit-notes/delete
 * Handles both draft (DB only) and posted (unapply + Zoho delete) credit notes
 * Accepts both internal numeric ids and Zoho ids
 */
export const deleteCreditNote = async (id: number | string): Promise<any> => {
  const token = getToken();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const response = await fetch(`${API_BASE_URL}/api/credit-notes/delete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ id }),
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to delete credit note",
      response.status,
      errorData
    );
  }

  return response.json();
};

// Export as API object for consistency
export const creditNotesAPI = {
  getCreditNoteById,
  updateCreditNoteJson,
  createZohoCreditNote,
  updateZohoCreditNote,
  rejectCreditNote,
  approveCreditNote,
  saveCreditNoteAsDraft,
  fetchCreditNotes,
  fetchCreditNoteCounts,

  deleteCreditNote,
};
